import React, { useRef, useState } from 'react';
import { Reorder, useDragControls } from 'framer-motion';
import TemplatePicker from './TemplatePicker';
import CreditEditor from './CreditEditor';

const ANIMATION_TYPES = [
  { value: 'fade', label: 'Fade' },
  { value: 'slide-left', label: 'Slide L' },
  { value: 'slide-right', label: 'Slide R' },
  { value: 'zoom-in', label: 'Zoom' },
  { value: 'none', label: 'None' },
];

/**
 * ControlsSidebar — left panel of the V2 editor.
 * Two tabs: "This Slide" (per-slide controls) and "All Slides & Post" (global).
 *
 * Props:
 *  - slides, selectedSlideIdx, onSelectSlide
 *  - onUpdateSlide(idx, patch)        – update current slide fields
 *  - onAddSlide(), onDeleteSlide(idx)
 *  - caption, hashtags, onSaveCaption(caption, hashtags)
 *  - onRegenerateCaption()
 *  - format, onFormatChange(f)
 *  - font, onFontChange(f)
 *  - channels, selectedChannelId, onChannelChange(id)
 *  - themes, onApplyTheme(themeId)
 *  - onRender(mode, opts), onExport(), onDelete()
 *  - onStatusChange(status)
 *  - availableTransitions, status
 *  - onUploadAsset(file, slideId)
 *  - onOpenImagePicker()
 *  - saving, rendering, exporting
 *  - gradientFrom, gradientTo, onGradientFromChange, onGradientToChange
 *  - post (full post object with story populated)
 *  - onRefreshFromStory()
 */
export default function ControlsSidebar({
  slides = [],
  selectedSlideIdx = 0,
  onSelectSlide,
  onUpdateSlide,
  onAddSlide,
  onDeleteSlide,
  onReorderSlides,
  caption = '',
  hashtags = '',
  onCaptionChange,
  onHashtagsChange,
  onSaveCaption,
  onRegenerateCaption,
  format = 'post',
  onFormatChange,
  font = 'display',
  onFontChange,
  channels = [],
  selectedChannelId = '',
  onChannelChange,
  themes = [],
  onApplyTheme,
  onRender,
  onExport,
  onQuickDownload,
  onDelete,
  onStatusChange,
  availableTransitions = [],
  status = 'draft',
  onUploadAsset,
  onOpenImagePicker,
  onOpenAddSlide,
  onOpenSchedule,
  onCreditChange,
  saving = false,
  rendering = false,
  exporting = false,
  gradientFrom = '#1a1a2e',
  gradientTo = '#0f3460',
  onGradientFromChange,
  onGradientToChange,
  post,
  onRefreshFromStory,
  canUndo = false,
  canRedo = false,
  onUndo,
  onRedo,
  lastEditedBy,
  onApplyToAll,
  onApplyGradient,
  onPublish,
  publishing = false,
  metaConnected = false,
}) {
  const fileInputRef = useRef(null);
  const [activeTab, setActiveTab] = useState('slide');
  const currentSlide = slides[selectedSlideIdx] || null;
  const story = post?.story;

  const handleFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (onUploadAsset) onUploadAsset(file, currentSlide?._id || null);
  };

  const handleBgImageUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onloadend = () => {
      onUpdateSlide(selectedSlideIdx, { backgroundImage: reader.result });
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const handleOverlayUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onloadend = () => {
      const existing = currentSlide?.overlayImages || [];
      onUpdateSlide(selectedSlideIdx, {
        overlayImages: [...existing, { url: reader.result, x: 50, y: 50, width: 30, height: 30, opacity: 1 }],
      });
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const handleOverlayUpdate = (idx, patch) => {
    const existing = [...(currentSlide?.overlayImages || [])];
    existing[idx] = { ...existing[idx], ...patch };
    onUpdateSlide(selectedSlideIdx, { overlayImages: existing });
  };

  const handleOverlayRemove = (idx) => {
    const existing = (currentSlide?.overlayImages || []).filter((_, i) => i !== idx);
    onUpdateSlide(selectedSlideIdx, { overlayImages: existing });
  };

  const handleReorder = (reorderedSlides) => {
    if (onReorderSlides) onReorderSlides(reorderedSlides);
  };

  const STATUS_LABELS = { draft: 'Draft', ready: 'Ready', approved: 'Approved', failed: 'Failed' };

  return (
    <div className="tw-w-full lg:tw-w-96 tw-flex tw-flex-col tw-gap-4 tw-overflow-y-auto tw-max-h-full tw-shrink-0 tw-pl-1">

      {/* ── Collaboration Indicator ── */}
      {lastEditedBy && (
        <div className="tw-flex tw-items-center tw-gap-2 tw-px-3 tw-py-2 tw-bg-blue-900/30 tw-border tw-border-blue-700/40 tw-rounded-lg tw-text-xs tw-text-blue-300">
          <span className="tw-w-2 tw-h-2 tw-rounded-full tw-bg-blue-400 tw-animate-pulse" />
          Last edited by {lastEditedBy}
        </div>
      )}

      {/* ── Story Source Info ── */}
      {story && (
        <Section title="📰 Story Source">
          <p className="tw-text-sm tw-text-zinc-300 tw-font-medium tw-leading-snug tw-mb-1">
            {story.title || story.subject || 'Untitled Story'}
          </p>
          {story.articleCount !== undefined && (
            <span className="tw-text-[10px] tw-text-zinc-500">{story.articleCount} articles</span>
          )}
          {onRefreshFromStory && (
            <button onClick={onRefreshFromStory} className="tw-btn-secondary tw-w-full tw-text-xs tw-mt-2">
              🔄 Refresh from Story
            </button>
          )}
        </Section>
      )}
      {/* ── Slide Thumbnails (Drag to Reorder) ── */}
      <Section title="📑 Slides" count={slides.length}>
        <Reorder.Group
          axis="x"
          values={slides.map((s, i) => s._id || `slide-${i}`)}
          onReorder={(reorderedIds) => {
            const idToSlide = new Map(slides.map((s, i) => [s._id || `slide-${i}`, s]));
            handleReorder(reorderedIds.map((rid) => idToSlide.get(rid)).filter(Boolean));
          }}
          className="tw-flex tw-flex-wrap tw-gap-2"
          as="div"
        >
          {slides.map((slide, idx) => (
            <DraggableThumb
              key={slide._id || `slide-${idx}`}
              slideId={slide._id || `slide-${idx}`}
              slide={slide}
              idx={idx}
              isSelected={idx === selectedSlideIdx}
              onSelect={() => { onSelectSlide(idx); setActiveTab('slide'); }}
            />
          ))}
        </Reorder.Group>
        <div className="tw-flex tw-gap-2 tw-mt-2">
          <button onClick={onOpenAddSlide || onAddSlide} className="tw-flex-1 tw-btn-secondary tw-text-xs">
            + Add Slide
          </button>
          <button
            onClick={() => onDeleteSlide(selectedSlideIdx)}
            disabled={slides.length <= 1}
            className="tw-flex-1 tw-btn-danger tw-text-xs disabled:tw-opacity-30"
          >
            Delete
          </button>
        </div>
        {/* Undo / Redo */}
        {(onUndo || onRedo) && (
          <div className="tw-flex tw-gap-2 tw-mt-2">
            <button onClick={onUndo} disabled={!canUndo} className="tw-flex-1 tw-btn-secondary tw-text-xs disabled:tw-opacity-30">
              ↩ Undo
            </button>
            <button onClick={onRedo} disabled={!canRedo} className="tw-flex-1 tw-btn-secondary tw-text-xs disabled:tw-opacity-30">
              ↪ Redo
            </button>
          </div>
        )}
      </Section>

      {/* ── Tab Bar ── */}
      <div className="tw-flex tw-rounded-lg tw-border tw-border-zinc-700 tw-bg-zinc-900 tw-p-0.5">
        <button
          onClick={() => setActiveTab('slide')}
          className={`tw-flex-1 tw-py-2 tw-text-xs tw-font-bold tw-rounded-md tw-transition-all ${
            activeTab === 'slide' ? 'tw-bg-zinc-700 tw-text-white' : 'tw-text-zinc-500 hover:tw-text-zinc-300'
          }`}
        >
          ✏️ This Slide
        </button>
        <button
          onClick={() => setActiveTab('post')}
          className={`tw-flex-1 tw-py-2 tw-text-xs tw-font-bold tw-rounded-md tw-transition-all ${
            activeTab === 'post' ? 'tw-bg-zinc-700 tw-text-white' : 'tw-text-zinc-500 hover:tw-text-zinc-300'
          }`}
        >
          📋 All Slides & Post
        </button>
      </div>

      {/* ════════ THIS SLIDE TAB ════════ */}

      {/* ── Slide Editor ── */}
      {activeTab === 'slide' && currentSlide && (
        <Section title={`✏️ Slide ${selectedSlideIdx + 1}`}>
          <label className="tw-label">Headline</label>
          <textarea
            value={currentSlide.headline || ''}
            onChange={(e) => onUpdateSlide(selectedSlideIdx, { headline: e.target.value })}
            className="tw-input tw-h-20 tw-resize-none"
            placeholder="Enter a catchy headline..."
          />

          <label className="tw-label tw-mt-3">Body Text</label>
          <textarea
            value={currentSlide.bodyText || ''}
            onChange={(e) => onUpdateSlide(selectedSlideIdx, { bodyText: e.target.value })}
            className="tw-input tw-h-28 tw-resize-none"
            placeholder="Brief body text for this slide..."
          />

          <div className="tw-grid tw-grid-cols-2 tw-gap-3 tw-mt-3">
            <div>
              <label className="tw-label">Background</label>
              <div className="tw-flex tw-items-center tw-gap-1">
                <input
                  type="color"
                  value={currentSlide.background || '#1a1a2e'}
                  onChange={(e) => onUpdateSlide(selectedSlideIdx, { background: e.target.value })}
                  className="tw-flex-1 tw-h-9 tw-rounded-lg tw-border tw-border-zinc-700 tw-bg-zinc-900 tw-cursor-pointer tw-p-0.5"
                />
                {onApplyToAll && slides.length > 1 && (
                  <button
                    onClick={() => onApplyToAll({ background: currentSlide.background || '#1a1a2e' })}
                    className="tw-text-[9px] tw-text-zinc-500 hover:tw-text-social-pink tw-whitespace-nowrap"
                    title="Apply this background to all slides"
                  >
                    All ↗
                  </button>
                )}
              </div>
            </div>
            <div>
              <label className="tw-label">Text Color</label>
              <div className="tw-flex tw-items-center tw-gap-1">
                <input
                  type="color"
                  value={currentSlide.textColor || '#ffffff'}
                  onChange={(e) => onUpdateSlide(selectedSlideIdx, { textColor: e.target.value })}
                  className="tw-flex-1 tw-h-9 tw-rounded-lg tw-border tw-border-zinc-700 tw-bg-zinc-900 tw-cursor-pointer tw-p-0.5"
                />
                {onApplyToAll && slides.length > 1 && (
                  <button
                    onClick={() => onApplyToAll({ textColor: currentSlide.textColor || '#ffffff' })}
                    className="tw-text-[9px] tw-text-zinc-500 hover:tw-text-social-pink tw-whitespace-nowrap"
                    title="Apply this text color to all slides"
                  >
                    All ↗
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Animation Type */}
          <label className="tw-label tw-mt-3">Animation</label>
          <div className="tw-flex tw-gap-1 tw-flex-wrap">
            {ANIMATION_TYPES.map((a) => (
              <button
                key={a.value}
                onClick={() => onUpdateSlide(selectedSlideIdx, { animationType: a.value })}
                className={`tw-px-2 tw-py-1 tw-text-[10px] tw-font-bold tw-uppercase tw-tracking-wider tw-rounded-lg tw-border tw-transition-all ${
                  (currentSlide.animationType || 'fade') === a.value
                    ? 'tw-bg-social-pink tw-border-social-pink tw-text-white'
                    : 'tw-border-zinc-700 tw-text-zinc-500 hover:tw-border-zinc-500'
                }`}
              >
                {a.label}
              </button>
            ))}
          </div>

          <label className="tw-label tw-mt-3">Background Image</label>
          <div className="tw-flex tw-gap-2">
            <label className="tw-btn-secondary tw-text-xs tw-cursor-pointer tw-flex-1 tw-text-center">
              📁 Upload
              <input type="file" accept="image/*" className="tw-hidden" onChange={handleBgImageUpload} />
            </label>
            {onOpenImagePicker && (
              <button onClick={onOpenImagePicker} className="tw-btn-secondary tw-text-xs tw-flex-1">
                🖼 Image Bank
              </button>
            )}
          </div>
          {currentSlide.backgroundImage && (
            <button
              onClick={() => onUpdateSlide(selectedSlideIdx, { backgroundImage: undefined })}
              className="tw-text-xs tw-text-zinc-500 hover:tw-text-red-400 tw-mt-1"
            >
              Remove background image
            </button>
          )}

          {/* ── Overlay Images ── */}
          <label className="tw-label tw-mt-3">Overlay Images</label>
          {(currentSlide.overlayImages || []).map((ov, idx) => (
            <div key={idx} className="tw-flex tw-items-center tw-gap-2 tw-mt-1 tw-p-2 tw-bg-zinc-800/50 tw-rounded-lg">
              <img src={ov.url} alt="" className="tw-w-10 tw-h-10 tw-rounded tw-object-cover" />
              <div className="tw-flex-1 tw-grid tw-grid-cols-2 tw-gap-1">
                <label className="tw-text-[9px] tw-text-zinc-500">X%
                  <input type="number" min={0} max={100} value={ov.x} onChange={(e) => handleOverlayUpdate(idx, { x: +e.target.value })} className="tw-input tw-text-xs tw-py-0.5 tw-mt-0.5" />
                </label>
                <label className="tw-text-[9px] tw-text-zinc-500">Y%
                  <input type="number" min={0} max={100} value={ov.y} onChange={(e) => handleOverlayUpdate(idx, { y: +e.target.value })} className="tw-input tw-text-xs tw-py-0.5 tw-mt-0.5" />
                </label>
                <label className="tw-text-[9px] tw-text-zinc-500">W%
                  <input type="number" min={5} max={100} value={ov.width} onChange={(e) => handleOverlayUpdate(idx, { width: +e.target.value })} className="tw-input tw-text-xs tw-py-0.5 tw-mt-0.5" />
                </label>
                <label className="tw-text-[9px] tw-text-zinc-500">Opacity
                  <input type="number" min={0} max={1} step={0.1} value={ov.opacity} onChange={(e) => handleOverlayUpdate(idx, { opacity: +e.target.value })} className="tw-input tw-text-xs tw-py-0.5 tw-mt-0.5" />
                </label>
              </div>
              <button onClick={() => handleOverlayRemove(idx)} className="tw-text-xs tw-text-red-400 hover:tw-text-red-300">✕</button>
            </div>
          ))}
          <label className="tw-btn-secondary tw-text-xs tw-cursor-pointer tw-w-full tw-text-center tw-mt-1">
            + Add Overlay
            <input type="file" accept="image/*" className="tw-hidden" onChange={handleOverlayUpload} />
          </label>

          {/* ── Element Positions ── */}
          <label className="tw-label tw-mt-3">Element Positions</label>
          <p className="tw-text-[9px] tw-text-zinc-500 tw-mb-1">Drag elements on the canvas or set X% / Y% below.</p>
          {[
            { key: 'content', label: '📝 Content' },
            { key: 'watermark', label: '📷 Watermark' },
            { key: 'counter', label: '🔢 Counter' },
          ].map(({ key, label }) => {
            const pos = currentSlide.elementPositions?.[key] || {};
            const defaults = { content: { x: 50, y: 45 }, watermark: { x: 14, y: 92 }, counter: { x: 88, y: 92 } };
            const def = defaults[key];
            return (
              <div key={key} className="tw-flex tw-items-center tw-gap-2 tw-mt-1">
                <span className="tw-text-[10px] tw-text-zinc-400 tw-w-24 tw-shrink-0">{label}</span>
                <label className="tw-text-[9px] tw-text-zinc-500 tw-flex-1">X
                  <input type="number" min={0} max={100} value={pos.x ?? def.x}
                    onChange={(e) => {
                      const positions = { ...(currentSlide.elementPositions || {}), [key]: { ...(pos || {}), x: +e.target.value, y: pos.y ?? def.y } };
                      onUpdateSlide(selectedSlideIdx, { elementPositions: positions });
                    }}
                    className="tw-input tw-text-xs tw-py-0.5 tw-mt-0.5" />
                </label>
                <label className="tw-text-[9px] tw-text-zinc-500 tw-flex-1">Y
                  <input type="number" min={0} max={100} value={pos.y ?? def.y}
                    onChange={(e) => {
                      const positions = { ...(currentSlide.elementPositions || {}), [key]: { ...(pos || {}), y: +e.target.value, x: pos.x ?? def.x } };
                      onUpdateSlide(selectedSlideIdx, { elementPositions: positions });
                    }}
                    className="tw-input tw-text-xs tw-py-0.5 tw-mt-0.5" />
                </label>
              </div>
            );
          })}
          <button
            onClick={() => onUpdateSlide(selectedSlideIdx, { elementPositions: {} })}
            className="tw-text-[10px] tw-text-zinc-500 hover:tw-text-social-pink tw-mt-1"
          >
            ↺ Reset all positions
          </button>

          {/* ── Credit Editor ── */}
          {currentSlide.asset && onCreditChange && (
            <CreditEditor
              credit={currentSlide.credit || {}}
              onChange={(c) => onCreditChange(currentSlide.asset, c)}
              className="tw-mt-3"
            />
          )}
        </Section>
      )}

      {activeTab === 'slide' && !currentSlide && (
        <div className="tw-text-center tw-text-zinc-500 tw-text-sm tw-py-8">
          No slide selected. Add a slide above.
        </div>
      )}

      {/* ════════ ALL SLIDES & POST TAB ════════ */}
      {activeTab === 'post' && (<>

      {/* ── Global Design ── */}
      <Section title="🎨 Global Design">
        <label className="tw-label">Format</label>
        <div className="tw-flex tw-rounded-lg tw-border tw-border-zinc-700 tw-bg-zinc-900 tw-p-0.5">
          {['post', 'story'].map((f) => (
            <button
              key={f}
              onClick={() => onFormatChange(f)}
              className={`tw-flex-1 tw-py-1.5 tw-text-xs tw-font-bold tw-rounded-md tw-transition-all ${
                format === f ? 'tw-bg-zinc-700 tw-text-white' : 'tw-text-zinc-500 hover:tw-text-zinc-300'
              }`}
            >
              {f === 'post' ? 'Post (1:1)' : 'Story (9:16)'}
            </button>
          ))}
        </div>

        <label className="tw-label tw-mt-3">Typography</label>
        <div className="tw-grid tw-grid-cols-3 tw-gap-2">
          {['display', 'sans', 'mono'].map((f) => (
            <button
              key={f}
              onClick={() => onFontChange(f)}
              className={`tw-py-1.5 tw-text-[10px] tw-font-bold tw-uppercase tw-tracking-widest tw-rounded-lg tw-border tw-transition-all ${
                font === f
                  ? 'tw-bg-social-pink tw-border-social-pink tw-text-white'
                  : 'tw-border-zinc-700 tw-text-zinc-500 hover:tw-border-zinc-500'
              }`}
            >
              {f}
            </button>
          ))}
        </div>

        {channels.length > 0 && (
          <>
            <label className="tw-label tw-mt-3">Channel</label>
            <select
              value={selectedChannelId}
              onChange={(e) => onChannelChange(e.target.value)}
              className="tw-input"
            >
              <option value="">— None —</option>
              {channels.map((ch) => (
                <option key={ch._id} value={ch._id}>
                  {ch.name} ({ch.handle}) — {ch.platform}
                </option>
              ))}
            </select>
          </>
        )}
      </Section>

      {/* ── Gradient ── */}
      <Section title="🌈 Gradient">
        <div className="tw-flex tw-items-center tw-gap-2">
          <div className="tw-text-center">
            <label className="tw-label">From</label>
            <input
              type="color"
              value={gradientFrom}
              onChange={(e) => onGradientFromChange(e.target.value)}
              className="tw-w-10 tw-h-8 tw-rounded tw-border tw-border-zinc-700 tw-cursor-pointer tw-p-0.5"
            />
          </div>
          <div
            className="tw-flex-1 tw-h-6 tw-rounded"
            style={{ background: `linear-gradient(to right, ${gradientFrom}, ${gradientTo})` }}
          />
          <div className="tw-text-center">
            <label className="tw-label">To</label>
            <input
              type="color"
              value={gradientTo}
              onChange={(e) => onGradientToChange(e.target.value)}
              className="tw-w-10 tw-h-8 tw-rounded tw-border tw-border-zinc-700 tw-cursor-pointer tw-p-0.5"
            />
          </div>
        </div>
        <p className="tw-text-[10px] tw-text-zinc-500 tw-mt-2">
          Interpolates colors across all {slides.length} slides
        </p>
        {onApplyGradient && (
          <button
            onClick={onApplyGradient}
            className="tw-btn-primary tw-w-full tw-text-xs tw-mt-2"
          >
            🎨 Apply Gradient to All Slides
          </button>
        )}
      </Section>

      {/* ── Caption & Hashtags ── */}
      <Section title="💬 Caption">
        <textarea
          value={caption}
          onChange={(e) => onCaptionChange(e.target.value)}
          className="tw-input tw-h-32 tw-resize-none"
          maxLength={2200}
          placeholder="Write an engaging caption..."
        />
        <div className="tw-text-xs tw-text-zinc-500 tw-text-right tw-mt-1">{caption.length} / 2,200</div>

        <label className="tw-label tw-mt-2"># Hashtags</label>
        <input
          type="text"
          value={hashtags}
          onChange={(e) => onHashtagsChange(e.target.value)}
          className="tw-input"
          placeholder="tag1, tag2, tag3"
        />
        <div className="tw-text-xs tw-text-zinc-500 tw-mt-1">Comma-separated</div>
      </Section>

      {/* ── Actions ── */}
      <Section title="⚡ Actions">
        <div className="tw-flex tw-gap-2">
          <button onClick={onSaveCaption} disabled={saving} className="tw-btn-primary tw-flex-1 tw-text-xs">
            {saving ? 'Saving...' : '💾 Save'}
          </button>
          <button onClick={onRegenerateCaption} disabled={saving} className="tw-btn-secondary tw-flex-1 tw-text-xs">
            🔄 Regenerate
          </button>
        </div>
        <div className="tw-flex tw-gap-2 tw-mt-2">
          <button onClick={() => onRender('images')} disabled={rendering} className="tw-btn-primary tw-flex-1 tw-text-xs">
            {rendering ? 'Rendering...' : '🖼 Render'}
          </button>
          <button onClick={onExport} disabled={exporting || status === 'failed'} className="tw-btn-secondary tw-flex-1 tw-text-xs">
            {exporting ? 'Exporting...' : '📦 Export ZIP'}
          </button>
        </div>
        {onQuickDownload && (
          <button onClick={onQuickDownload} className="tw-btn-secondary tw-w-full tw-text-xs tw-mt-2">
            ⬇️ Quick Download (PNG)
          </button>
        )}
        {onOpenSchedule && (
          <button onClick={onOpenSchedule} className="tw-btn-secondary tw-w-full tw-text-xs tw-mt-2">
            📅 Schedule Post
          </button>
        )}
        {onPublish && metaConnected && (
          <button
            onClick={onPublish}
            disabled={publishing || status === 'failed'}
            className="tw-w-full tw-text-xs tw-mt-2 tw-py-2 tw-px-3 tw-rounded-lg tw-font-semibold tw-bg-blue-600 hover:tw-bg-blue-500 tw-text-white disabled:tw-opacity-40 tw-transition-colors"
          >
            {publishing ? 'Publishing...' : '🚀 Publish to Meta'}
          </button>
        )}
        {onPublish && !metaConnected && (
          <div className="tw-mt-2 tw-p-2 tw-rounded-lg tw-bg-zinc-800/50 tw-border tw-border-zinc-700 tw-text-center">
            <p className="tw-text-[10px] tw-text-zinc-400 tw-mb-1">Meta not connected</p>
            <p className="tw-text-[10px] tw-text-zinc-500">Connect via Admin → Social Posts → Channels</p>
          </div>
        )}
        <button
          onClick={() => fileInputRef.current?.click()}
          className="tw-btn-secondary tw-w-full tw-text-xs tw-mt-2"
        >
          📤 Upload Image Asset
        </button>
        <input ref={fileInputRef} type="file" accept="image/*" className="tw-hidden" onChange={handleFileUpload} />
      </Section>

      {/* ── Templates ── */}
      {themes.length > 0 && (
        <Section title="🧩 Templates">
          <p className="tw-text-[10px] tw-text-zinc-500 tw-mb-2">
            Applies theme colors to all slides
          </p>
          <TemplatePicker themes={themes} onApplyTheme={onApplyTheme} currentThemeId={post?.theme} />
        </Section>
      )}

      {/* ── Status ── */}
      {availableTransitions.length > 0 && (
        <Section title="📊 Status">
          <div className="tw-flex tw-gap-2 tw-flex-wrap">
            {availableTransitions.map((s) => (
              <button
                key={s}
                onClick={() => onStatusChange(s)}
                className="tw-btn-secondary tw-text-xs"
              >
                Move to {STATUS_LABELS[s]}
              </button>
            ))}
          </div>
        </Section>
      )}

      {/* ── Danger Zone ── */}
      <Section title="⚠️ Danger Zone">
        <button onClick={onDelete} className="tw-btn-danger tw-w-full tw-text-xs">
          🗑 Delete Post
        </button>
      </Section>
      </>)}
    </div>
  );
}

function Section({ title, count, children }) {
  const [open, setOpen] = useState(true);
  return (
    <div className="tw-bg-zinc-900/50 tw-rounded-xl tw-border tw-border-zinc-800 tw-p-4">
      <button
        onClick={() => setOpen(!open)}
        className="tw-w-full tw-flex tw-items-center tw-justify-between tw-text-xs tw-font-bold tw-uppercase tw-tracking-widest tw-text-zinc-500 hover:tw-text-zinc-300 tw-transition-colors"
      >
        <span>{title}{count !== undefined ? ` (${count})` : ''}</span>
        <span className="tw-text-zinc-600">{open ? '▾' : '▸'}</span>
      </button>
      {open && <div className="tw-mt-3">{children}</div>}
    </div>
  );
}

function DraggableThumb({ slideId, slide, idx, isSelected, onSelect }) {
  const controls = useDragControls();
  return (
    <Reorder.Item
      value={slideId}
      dragControls={controls}
      dragListener={false}
      as="div"
      className={`tw-relative tw-w-14 tw-h-14 tw-rounded-lg tw-border-2 tw-cursor-pointer tw-overflow-hidden tw-flex-shrink-0 tw-transition-all ${
        isSelected ? 'tw-border-social-pink tw-ring-2 tw-ring-social-pink/30' : 'tw-border-zinc-700 hover:tw-border-zinc-500'
      }`}
      onClick={onSelect}
    >
      <div
        className="tw-absolute tw-inset-0"
        style={{ background: slide.backgroundImage ? `url(${slide.backgroundImage}) center/cover` : (slide.background || '#1a1a2e') }}
      />
      <span className="tw-absolute tw-bottom-0 tw-right-0 tw-bg-black/70 tw-text-[8px] tw-px-1 tw-text-zinc-300 tw-font-bold">{idx + 1}</span>
      <span
        onPointerDown={(e) => controls.start(e)}
        className="tw-absolute tw-top-0 tw-left-0 tw-bg-black/50 tw-text-[8px] tw-px-1 tw-cursor-grab active:tw-cursor-grabbing tw-text-zinc-400"
        title="Drag to reorder"
      >
        ⠿
      </span>
    </Reorder.Item>
  );
}
